/**
 * Карточка квеста с действиями модерации.
 *
 * Модератор должен видеть источник каждого снимка и иметь возможность
 * отклонить квест с комментарием — комментарий показывается автору.
 */
import { useState } from "react";
import { Show } from "@refinedev/antd";
import { useCustomMutation, useInvalidate, useShow } from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Image,
  Input,
  Modal,
  Space,
  Tag,
  Typography,
  message,
} from "antd";

import { formatCoordinates, formatYear, questTypeLabel, statusMeta } from "../../lib/constants";

interface Photo {
  id: string;
  imageUrl?: string;
  description?: string;
  year?: number;
  sourceUrl?: string;
  status?: string;
  location?: { latitude?: number; longitude?: number };
}

interface Quest {
  id: string;
  title?: string;
  description?: string;
  type?: string;
  status?: string;
  tags?: string[];
  activeDate?: string;
  moderationComment?: string;
  photos?: Photo[];
  roundCount?: number;
  createdBy?: { username?: string };
}

export function QuestShow() {
  const { query, result: record } = useShow<Quest>({ resource: "quests" });

  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation();
  const isModerating = mutation.isPending;

  const [rejectOpen, setRejectOpen] = useState(false);
  const [comment, setComment] = useState("");

  const moderate = (approve: boolean, moderationComment: string) => {
    mutate(
      {
        url: `/api/v1/admin/quests/${record?.id}/moderate`,
        method: "post",
        values: { id: record?.id, approve, comment: moderationComment },
      },
      {
        onSuccess: () => {
          message.success(approve ? "Квест опубликован" : "Квест отклонён");
          setRejectOpen(false);
          setComment("");
          void invalidate({ resource: "quests", invalidates: ["detail", "list"], id: record?.id });
        },
        onError: (error) => message.error(error.message),
      },
    );
  };

  const meta = statusMeta(record?.status);
  const isPending = record?.status === "CONTENT_STATUS_DRAFT";

  return (
    <Show
      isLoading={query.isLoading}
      title="Квест"
      headerButtons={
        <Space>
          <Button
            type="primary"
            loading={isModerating}
            disabled={record?.status === "CONTENT_STATUS_PUBLISHED"}
            onClick={() => moderate(true, "")}
          >
            Опубликовать
          </Button>
          <Button danger loading={isModerating} onClick={() => setRejectOpen(true)}>
            Отклонить
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {isPending && (
          <Alert
            type="warning"
            showIcon
            message="Квест ждёт модерации"
            description="Проверьте источники снимков: ссылка на страницу, где найдено фото, ещё не означает права на публикацию."
          />
        )}

        {record?.moderationComment && (
          <Alert type="info" showIcon message="Комментарий модератора" description={record.moderationComment} />
        )}

        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Название" span={2}>
            <Typography.Text strong>{record?.title}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Описание" span={2}>
            {record?.description || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Тип">{questTypeLabel(record?.type)}</Descriptions.Item>
          <Descriptions.Item label="Дата">{record?.activeDate || "—"}</Descriptions.Item>
          <Descriptions.Item label="Статус">
            {meta ? <Tag color={meta.color}>{meta.label}</Tag> : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Автор">
            {record?.createdBy?.username ?? "редакция"}
          </Descriptions.Item>
          <Descriptions.Item label="Теги" span={2}>
            {record?.tags?.length ? record.tags.map((tag) => <Tag key={tag}>{tag}</Tag>) : "—"}
          </Descriptions.Item>
        </Descriptions>

        <Typography.Title level={5}>
          Раунды ({(record?.roundCount ?? record?.photos?.length ?? 0).toLocaleString("ru-RU")})
        </Typography.Title>
        {record?.type === "QUEST_TYPE_WORLD" && (
          <Typography.Paragraph type="secondary">
            Все опубликованные снимки: состав собирается автоматически, поэтому список здесь не показывается.
          </Typography.Paragraph>
        )}

        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {record?.photos?.map((photo, index) => (
            <Card key={photo.id} size="small">
              <Space align="start" size={16} wrap>
                {photo.imageUrl && (
                  <Image
                    src={photo.imageUrl}
                    width={140}
                    height={100}
                    style={{ objectFit: "cover", borderRadius: 4 }}
                  />
                )}

                <Space direction="vertical" size={2}>
                  <Typography.Text strong>
                    Раунд {index + 1}: {photo.description || "без описания"}
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    {formatYear(photo.year)} ·{" "}
                    {formatCoordinates(photo.location?.latitude, photo.location?.longitude)}
                  </Typography.Text>
                  {photo.sourceUrl ? (
                    <Typography.Link href={photo.sourceUrl} target="_blank">
                      Источник снимка
                    </Typography.Link>
                  ) : (
                    <Typography.Text type="danger">Источник не указан</Typography.Text>
                  )}
                  {statusMeta(photo.status) && (
                    <Tag color={statusMeta(photo.status)!.color}>
                      {statusMeta(photo.status)!.label}
                    </Tag>
                  )}
                </Space>
              </Space>
            </Card>
          ))}
        </Space>
      </Space>

      <Modal
        open={rejectOpen}
        title="Отклонить квест"
        okText="Отклонить"
        cancelText="Отмена"
        okButtonProps={{ danger: true, disabled: comment.trim().length === 0 }}
        confirmLoading={isModerating}
        onOk={() => moderate(false, comment.trim())}
        onCancel={() => setRejectOpen(false)}
      >
        <Typography.Paragraph type="secondary">
          Причина обязательна — она показывается автору квеста.
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder="Например: не указан источник снимков"
        />
      </Modal>
    </Show>
  );
}
